import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Users, X } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';

export type InstagramUserTag = {
  username: string;
  x: number;
  y: number;
};

export type InstagramComposeOptions = {
  locationId?: string;
  locationName?: string;
  collaborators?: string[];
  userTags?: InstagramUserTag[];
};

type LocationHit = {
  id: string;
  name: string;
  locationLabel: string | null;
};

export type InstagramExtrasSection = 'location' | 'collaborators' | 'tags';

export interface InstagramPostExtrasProps {
  businessId: string;
  connectionId: string;
  /** People tags only work on single-image posts. */
  allowPeopleTags: boolean;
  /** Preview URL for tap-to-place people tags (first slide). */
  previewUrl?: string | null;
  value: InstagramComposeOptions;
  onChange: (next: InstagramComposeOptions) => void;
  disabled?: boolean;
  /**
   * Render only one section, without the card wrapper or section labels —
   * for accordion-row layouts where the parent renders the row header.
   */
  section?: InstagramExtrasSection;
}

function stripAt(raw: string): string {
  return raw.trim().replace(/^@+/, '').replace(/\s+/g, '');
}

/**
 * Instagram native extras: location tag, collaborators (≤3), people tags (single image).
 */
export default function InstagramPostExtras({
  businessId,
  connectionId,
  allowPeopleTags,
  previewUrl,
  value,
  onChange,
  disabled = false,
  section,
}: InstagramPostExtrasProps) {
  const showSection = (s: InstagramExtrasSection) => !section || section === s;
  const [locationQuery, setLocationQuery] = useState('');
  const [locationHits, setLocationHits] = useState<LocationHit[]>([]);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [collabInput, setCollabInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [placingUsername, setPlacingUsername] = useState<string | null>(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    const q = locationQuery.trim();
    if (q.length < 2) {
      setLocationHits([]);
      setSearchingLocation(false);
      setLocationError(null);
      return;
    }
    const seq = ++searchSeq.current;
    setSearchingLocation(true);
    setLocationError(null);
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const { data, error } = await supabase.functions.invoke('ayrshare-instagram-posts', {
            body: {
              action: 'search_location',
              businessId,
              connectionId,
              search: q,
            },
          });
          if (seq !== searchSeq.current) return;
          if (error || data?.error) {
            setLocationHits([]);
            setLocationError(
              data?.error ||
                (await invokeErrorMessage(error, data)) ||
                'Location search failed',
            );
            return;
          }
          setLocationHits(Array.isArray(data?.locations) ? data.locations : []);
        } catch (err) {
          if (seq !== searchSeq.current) return;
          setLocationHits([]);
          setLocationError(err instanceof Error ? err.message : 'Location search failed');
        } finally {
          if (seq === searchSeq.current) setSearchingLocation(false);
        }
      })();
    }, 350);
    return () => window.clearTimeout(t);
  }, [locationQuery, businessId, connectionId]);

  // Drop people tags when switching to a carousel.
  useEffect(() => {
    if (!allowPeopleTags && value.userTags?.length) {
      onChange({ ...value, userTags: undefined });
      setPlacingUsername(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to allowPeopleTags flips
  }, [allowPeopleTags]);

  const collaborators = value.collaborators || [];
  const userTags = value.userTags || [];

  const addCollaborator = () => {
    const username = stripAt(collabInput);
    if (!username || collaborators.length >= 3 || collaborators.includes(username)) {
      setCollabInput('');
      return;
    }
    onChange({ ...value, collaborators: [...collaborators, username].slice(0, 3) });
    setCollabInput('');
  };

  const startPeopleTag = () => {
    const username = stripAt(tagInput);
    if (!username || !allowPeopleTags) return;
    if (userTags.some((t) => t.username.toLowerCase() === username.toLowerCase())) {
      setTagInput('');
      return;
    }
    if (previewUrl) {
      setPlacingUsername(username);
      setTagInput('');
      return;
    }
    onChange({
      ...value,
      userTags: [...userTags, { username, x: 0.5, y: 0.5 }],
    });
    setTagInput('');
  };

  const placeTagAt = (clientX: number, clientY: number, el: HTMLElement) => {
    if (!placingUsername) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const x = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    onChange({
      ...value,
      userTags: [...userTags, { username: placingUsername, x, y }],
    });
    setPlacingUsername(null);
  };

  return (
    <div
      className={
        section
          ? 'space-y-4'
          : 'space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-3 sm:p-4'
      }
    >
      {!section ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Instagram options
        </p>
      ) : null}

      {/* Location */}
      {showSection('location') ? (
      <div>
        {!section ? (
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <MapPin className="h-3.5 w-3.5 text-gray-500" />
            Location
          </label>
        ) : null}
        {value.locationId ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {value.locationName || value.locationId}
              </p>
              <p className="text-[11px] text-gray-400 truncate">ID {value.locationId}</p>
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({ ...value, locationId: undefined, locationName: undefined })
              }
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              aria-label="Remove location"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              disabled={disabled}
              placeholder="Search places with a physical address…"
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50"
            />
            {searchingLocation ? (
              <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />
            ) : null}
            {locationHits.length > 0 ? (
              <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {locationHits.map((hit) => (
                  <li key={hit.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        onChange({
                          ...value,
                          locationId: hit.id,
                          locationName: hit.name,
                        });
                        setLocationQuery('');
                        setLocationHits([]);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 disabled:opacity-50"
                    >
                      <p className="text-sm font-medium text-gray-900">{hit.name}</p>
                      {hit.locationLabel ? (
                        <p className="text-xs text-gray-500">{hit.locationLabel}</p>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {locationError ? (
              <p className="text-xs text-red-600 mt-1">{locationError}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Only Facebook Pages with a public location can be tagged.
              </p>
            )}
          </div>
        )}
      </div>
      ) : null}

      {/* Collaborators */}
      {showSection('collaborators') ? (
      <div>
        {!section ? (
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
            <Users className="h-3.5 w-3.5 text-gray-500" />
            Collaborators
            <span className="font-normal text-gray-400">({collaborators.length}/3)</span>
          </label>
        ) : null}
        {collaborators.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {collaborators.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700"
              >
                @{c}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onChange({
                      ...value,
                      collaborators: collaborators.filter((x) => x !== c),
                    })
                  }
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                  aria-label={`Remove @${c}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        {collaborators.length < 3 ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={collabInput}
              onChange={(e) => setCollabInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCollaborator();
                }
              }}
              disabled={disabled}
              placeholder="@username"
              className="flex-1 h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50"
            />
            <button
              type="button"
              disabled={disabled || !stripAt(collabInput)}
              onClick={addCollaborator}
              className="h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        ) : null}
        <p className="text-xs text-gray-400 mt-1">
          Public Instagram accounts only. They must accept the invite in the app.
        </p>
      </div>
      ) : null}

      {/* People tags */}
      {showSection('tags') ? (
      <div>
        {!section ? (
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Tag people</label>
        ) : null}
        {!allowPeopleTags ? (
          <p className="text-xs text-gray-500">
            People tags are only available on single-image posts (not carousels).
          </p>
        ) : (
          <>
            {userTags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {userTags.map((t) => (
                  <span
                    key={`${t.username}-${t.x}-${t.y}`}
                    className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700"
                  >
                    @{t.username}
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() =>
                        onChange({
                          ...value,
                          userTags: userTags.filter(
                            (x) =>
                              !(
                                x.username === t.username &&
                                x.x === t.x &&
                                x.y === t.y
                              ),
                          ),
                        })
                      }
                      className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                      aria-label={`Remove @${t.username}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    startPeopleTag();
                  }
                }}
                disabled={disabled || !!placingUsername}
                placeholder="@username"
                className="flex-1 h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50"
              />
              <button
                type="button"
                disabled={disabled || !stripAt(tagInput) || !!placingUsername}
                onClick={startPeopleTag}
                className="h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {previewUrl ? 'Place' : 'Add'}
              </button>
            </div>
            {placingUsername && previewUrl ? (
              <div className="mt-2">
                <p className="text-xs text-blue-700 mb-1.5 font-medium">
                  Tap the photo to place @{placingUsername}
                  <button
                    type="button"
                    className="ml-2 underline text-gray-500 font-normal"
                    onClick={() => setPlacingUsername(null)}
                  >
                    Cancel
                  </button>
                </p>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={(e) => placeTagAt(e.clientX, e.clientY, e.currentTarget)}
                  className="relative w-full max-w-[220px] aspect-square rounded-lg overflow-hidden border-2 border-blue-400 ring-2 ring-blue-100 cursor-crosshair"
                >
                  <img
                    src={previewUrl}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                    draggable={false}
                  />
                  {userTags.map((t) => (
                    <span
                      key={`dot-${t.username}-${t.x}-${t.y}`}
                      className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500 ring-2 ring-white"
                      style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
                      title={`@${t.username}`}
                    />
                  ))}
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                {previewUrl
                  ? 'Add a username, then tap the photo to place the tag.'
                  : 'Tags are placed at the center of the image.'}
              </p>
            )}
          </>
        )}
      </div>
      ) : null}
    </div>
  );
}

/** Build payload for stage/publish (omit empty). */
export function toInstagramOptionsPayload(
  value: InstagramComposeOptions,
  allowPeopleTags: boolean,
): InstagramComposeOptions | null {
  const collaborators = (value.collaborators || [])
    .map(stripAt)
    .filter(Boolean)
    .slice(0, 3);
  const userTags =
    allowPeopleTags && value.userTags?.length
      ? value.userTags
          .map((t) => ({
            username: stripAt(t.username),
            x: Number.isFinite(t.x) ? Math.min(1, Math.max(0, t.x)) : 0.5,
            y: Number.isFinite(t.y) ? Math.min(1, Math.max(0, t.y)) : 0.5,
          }))
          .filter((t) => t.username)
      : [];
  const locationId = String(value.locationId || '').trim();
  const locationName = String(value.locationName || '').trim();
  if (!locationId && collaborators.length === 0 && userTags.length === 0) return null;
  return {
    ...(locationId ? { locationId, ...(locationName ? { locationName } : {}) } : {}),
    ...(collaborators.length ? { collaborators } : {}),
    ...(userTags.length ? { userTags } : {}),
  };
}
